import React, { useCallback } from 'react';
import { styled, SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import { FlatList, IFlatListProps, Separator } from '@components';
import { useAsyncInit } from '@hooks';
import { AnalyticsService, ToastService } from '@services';
import { parseApiError } from '@utils';
import { Tutorial } from '@models/Tutorials/TutorialModel';
import {
  HubNavigatorNavigationPropType,
  HubNavigatorRoutePropType,
  screens,
} from '@navigation';
import { PlaylistItem } from './PlaylistItem';
import { useTutorialsModel } from '@models/Tutorials/TutorialsModel';

const List = styled<IFlatListProps<Tutorial>>(FlatList)({
  flex: 1,
});

function contentContainerStyle(props: EdgeInsets): SxProp {
  return (theme) => ({
    px: ['$6', '$7', '$0'],
    pb: theme.space.$6 + props.bottom,
  });
}

export interface IPlaylistProps
  extends Omit<
    IFlatListProps<Tutorial>,
    'data' | 'renderItem' | 'keyExtractor' | 'getItemLayout'
  > {
  tutorial: Tutorial;
}

type NavigationPropType = HubNavigatorNavigationPropType<'Tutorial'>;
type RoutePropType = HubNavigatorRoutePropType<'Tutorial'>;

export const TutorialPlaylist = observer<IPlaylistProps>(
  ({ ...props }) => {
    const insets = useSafeAreaInsets();
    const nav = useNavigation<NavigationPropType>();
    const { params } = useRoute<RoutePropType>();
    const tutorials = useTutorialsModel();

    useAsyncInit(async () => {
      try {
        await tutorials.related.fetch.run(params);
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    });

    async function fetchMore() {
      try {
        await tutorials.related.fetchMore.run(params);
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    function trackTutorialAnalytic(item: Tutorial) {
      AnalyticsService.trackRecommendationContent(item.title);
    }

    function navigateToTutorial(item: Tutorial) {
      nav.replace(screens.Tutorial, {
        id: item.id,
        extraCategoryId: params.extraCategoryId,
      });
    }

    const onTutorialPress = useCallback(
      (item: Tutorial) => {
        if (item.id === params.id) {
          return;
        }

        navigateToTutorial(item);
        trackTutorialAnalytic(item);
      },
      [params.id],
    );

    return (
      <List
        {...props}
        contentContainerSx={contentContainerStyle(insets)}
        data={tutorials.related.asArray}
        onEndReached={fetchMore}
        renderItem={({ item }) => (
          <PlaylistItem item={item} onPress={onTutorialPress} />
        )}
        keyExtractor={(i) => i.id.toString()}
        ItemSeparatorComponent={() => (
          <Separator size={['$6', '$8']} />
        )}
      />
    );
  },
);
